<template>
  <div class="airport-search-input">
    <div class="airport-search-input__icon">
      <vb-icon :name="icon" :color="sass('primary')" :size="20" />
    </div>
    <autocomplete
      :ref="wrapper_ref_name"
      base-class="airport-search-input__autocomplete"
      :search="search_airports"
      :placeholder="active_placeholder"
      :debounce-time="500"
      :get-result-value="get_result_value"
      type="search"
      @submit="on_submit"
      @focus="on_focus"
      @blur="on_blur"
    >
      <template v-slot:result="{ result, props }">
        <li
          :key="props.id"
          v-bind="props"
          :data-type="result.type"
          class="airport-search-input__result-item"
        >
          <div style="margin-top: 4px">
            <vb-icon
              :name="result.type !== 'airport' ? 'pin' : 'airplane'"
              :size="16"
            />
          </div>
          <span v-html="result.name_full"></span>
        </li>
      </template>
    </autocomplete>
    <div v-if="is_searching" class="airport-search-input__loading">
      <loader />
    </div>
  </div>
</template>

<script>
  import Autocomplete from '@trevoreyre/autocomplete-vue';
  import '@trevoreyre/autocomplete-vue/dist/style.css';
  import { service_handler } from '@/services';
  import { stripHtmlTags } from './logic';

  export default {
    name: 'airport-search-input',
    components: {
      autocomplete: Autocomplete,
    },
    props: {
      value: {
        type: String,
        default: '',
      },
      placeholder: {
        type: String,
        required: true,
      },
      default_placeholder: {
        type: String,
        default: '',
      },
      icon: {
        type: String,
        default: 'pin',
      },
      ref_name: {
        type: String,
        required: true,
      },
      filtered_iata_codes: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        is_focused: false,
        is_searching: false,
      };
    },
    computed: {
      wrapper_ref_name() {
        return `wrapper:${this.ref_name}`;
      },
      active_placeholder() {
        if (this.is_focused && this.default_placeholder) {
          return this.default_placeholder;
        }
        return this.placeholder;
      },
    },
    mounted() {
      // This is to avoid passing the listeners but keeping the deep input style
      this.$nextTick(() => {
        const autocomplete = this.$refs[this.wrapper_ref_name];
        if (autocomplete && autocomplete.$refs.input) {
          autocomplete.$refs.input.classList.add('vb-input', 'form-control');
        }
      });
    },
    methods: {
      search_airports(q) {
        if (!q || q === '' || q === this.placeholder) return [];
        if (q.length < 3) {
          return Promise.resolve([]);
        }
        this.is_searching = true;
        return service_handler(
          'locations',
          {
            q,
            lang: 'IT',
          },
          { modal: false, cancel_previous: true }
        )
          .then(
            (response) => {
              let suggestions =
                response.data && response.data.suggestions
                  ? response.data.suggestions
                  : [];

              suggestions = suggestions.filter(
                (s) => !!s.iata_code && !this.filtered_iata_codes[s.iata_code]
              );

              if (suggestions.length === 0) {
                suggestions = [
                  {
                    type: 'noresults',
                    iata_code: '',
                    name_full: 'nessun risultato',
                  },
                ];
              }

              return suggestions;
            },
            () => [
              {
                type: 'noresults',
                iata_code: '',
                name_full: 'si è verificato un problema con la ricerca',
              },
            ]
          )
          .finally(() => {
            this.is_searching = false;
          });
      },
      get_result_value(result) {
        return stripHtmlTags(result.name_full);
      },
      on_submit(result) {
        const { iata_code, name_full } = result || {};
        if (iata_code && name_full) {
          this.$emit('select', {
            iata_code,
            name_full,
          });
        }
        this.$nextTick(() => {
          const autocomplete = this.$refs[this.wrapper_ref_name];
          if (autocomplete) {
            autocomplete.setValue(null);
          }
        });
      },
      on_focus(event) {
        this.is_focused = true;
        this.$emit('focus', event);
        event.target.select();
      },
      on_blur() {
        this.is_focused = false;
        this.$emit('blur');
        const autocomplete = this.$refs[this.wrapper_ref_name];
        if (autocomplete) {
          autocomplete.setValue(null);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .airport-search-input {
    font-size: $font-size-base;
    position: relative;

    &__icon {
      position: absolute;
      top: 21px;
      left: 10px;
      height: 26px;
      transform: translateY(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1;
    }

    ::v-deep .airport-search-input__autocomplete-input {
      padding-left: 40px;
      border: 1px solid var(--primary);
      font-size: $font-size-base;

      &::placeholder {
        font-size: $font-size-base;
        color: var(--text);
      }
    }

    ::v-deep .airport-search-input__autocomplete-result-list {
      margin: 5px 0 0 0;
      padding: 0;
      background-color: $white;
      border: 1px solid $gray-200;
      box-shadow: $box-shadow-sm;
      border-radius: $btn-border-radius;
      list-style: none;
      max-height: 250px;
      overflow: auto;
      z-index: 10 !important;

      @include media-breakpoint-down(sm) {
        position: static !important;
        z-index: unset !important;
        margin-top: 0;
        width: 100% !important;
      }

      @include media-breakpoint-between(sm, md) {
        width: unset !important;
      }
    }

    &__loading {
      position: absolute;
      top: 50%;
      right: $grid-gutter-width / 4;
      transform: translateY(-50%);
      z-index: 1;
      background-color: $white;
    }

    &__result-item {
      display: flex;
      align-items: flex-start;
      gap: 5px;
      padding: 5px 0;
      cursor: pointer;
      transition: $transition-base;
      padding: $grid-gutter-width / 2;
      span {
        line-height: normal;
      }
      ::v-deep svg {
        transition: $transition-base;
      }

      &:hover {
        color: var(--info);
        ::v-deep svg {
          --color: var(--info);
          color: var(--info);
          fill: var(--info);
        }
      }
      &:not(:last-child) {
        border-bottom: 1px solid $gray-200;
      }
    }
  }
</style>
